<template>
  <div>
    <a-table
      :columns="columns"
      :data-source="mergeContents"
      :loading="mergeContentsLoading"
      :rowKey="record => record.id"
      :pagination="{
        total: totalMergeContents,
        onChange: (page, pageSize) => {
          fetchMergeContents(page - 1, pageSize);
        }
      }"
    >
      <template #expandedRowRender="{record}">
        <p style="margin: 0">
          <dynamic-content-rule :rule="record.rule" />
        </p>
      </template>

      <template #action="{record}">
        <div
          style="
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 8px;
          "
        >
          <a-dropdown :trigger="['click']">
            <template #overlay>
              <a-menu>
                <a-menu-item key="edit" @click="handleEditContent(record)">
                  {{ $t('general.edit') }}
                </a-menu-item>
                <a-menu-item key="delete" @click="handleDeleteContent(record)">
                  {{ $t('general.delete') }}
                </a-menu-item>
              </a-menu>
            </template>
            <a-button shape="circle" size="small" type="default">
              <MoreOutlined />
            </a-button>
          </a-dropdown>

          <a-tooltip v-if="record.hasWarning" placement="top">
            <template #title>
              <span>{{ $t('content.contents_extra_info_error_title') }}</span>
            </template>
            <WarningFilled style="color: #faad14; font-size: 18px" />
          </a-tooltip>
        </div>
      </template>
    </a-table>

    <a-modal
      v-model:visible="modalVisible"
      :title="modalTitle"
      @ok="deleteContent"
    >
      <p>
        <StrongI18n
          keypath="content.delete_content"
          :values="{propName: deleteName}"
        />
      </p>
    </a-modal>
  </div>
</template>

<script>
import {mapState} from 'vuex';
import DynamicContentRule from './DynamicContentRule.vue';
import StrongI18n from '../lang/StrongI18n.vue';

export default {
  name: 'DynamicContentsList',
  components: {
    DynamicContentRule,
    StrongI18n
  },
  props: ['mergeContentService'],
  computed: {
    ...mapState({
      customer: state => state.customer,
      accessToken: state => state.accessToken
    }),
    deleteName() {
      return this.selectedContent?.name || '';
    }
  },
  data() {
    return {
      modalVisible: false,
      selectedContent: undefined,
      modalTitle: this.$t('content.delete_content_title'),
      mergeContents: [],
      mergeContentsLoading: false,
      totalMergeContents: 0,
      columns: [
        {
          title: this.$t('general.name').toUpperCase(),
          dataIndex: 'name',
          key: 'name'
        },
        {
          title: this.$t('content.source').toUpperCase(),
          dataIndex: 'external-source-name',
          key: 'external-source-name'
        },
        {
          title: this.$t('content.view').toUpperCase(),
          dataIndex: 'view-name',
          key: 'view-name'
        },
        {
          title: this.$t('general.database').toUpperCase(),
          dataIndex: 'rule.data.value.data[merge-tag-source-name]',
          align: 'center'
        },
        {
          title: this.$t('general.version_date').toUpperCase(),
          dataIndex: 'version-date',
          key: 'version-date'
        },
        {
          title: this.$t('general.version_by').toUpperCase(),
          dataIndex: 'version-by',
          key: 'version-by',
          align: 'center'
        },
        {
          title: this.$t('filters.list_actions').toUpperCase(),
          key: 'x',
          dataIndex: '',
          slots: {customRender: 'action'}
        }
      ]
    };
  },
  mounted() {
    this.fetchMergeContents(0, 10);
  },
  methods: {
    async fetchMergeContents(page, pageSize) {
      this.mergeContentsLoading = true;
      try {
        const res = await this.mergeContentService.fetchMergeContents(
          this.accessToken,
          this.customer,
          {page, pageSize}
        );

        this.mergeContents = res.content.map(c => {
          if (c.rule.data.value.data['merge-tag-source-name'] === undefined) {
            c.rule.data.value.data['merge-tag-source-name'] = '-';
          }
          return c;
        });

        this.totalMergeContents = res.totalElements;

        if (res.errors?.length) {
          this.showError(
            this.$t('content.contents_extra_info_error_title'),
            res.errors.at(-1).message
          );
        }
      } catch (error) {
        this.mergeContents = [];
        this.showError(this.$t('content.contents_error_title'), error.message);
      } finally {
        this.mergeContentsLoading = false;
      }
    },
    handleEditContent(content) {
      this.$emit('edit', content);
    },
    handleDeleteContent(content) {
      this.selectedContent = content;
      this.modalVisible = true;
    },
    deleteContent() {
      this.mergeContentService
        .deleteMergeContent(
          this.accessToken,
          this.customer,
          this.selectedContent.id
        )
        .then(() => this.fetchMergeContents(0, 10))
        .catch(() =>
          this.showError('', this.$t('content.delete_content_error'))
        )
        .finally(() => (this.modalVisible = false));
    },
    showError(message, description) {
      this.$notification.error({message, description});
    }
  }
};
</script>

<style>
.ant-table-thead > tr > th {
  font-weight: bold !important;
}

.anticon-warning {
  margin-right: 6px;
  font-size: 24px;
}
</style>
